import { useRef, useState } from 'react'

import { Box, Field, Flex, Input, InputGroup, Slider, Text } from '@chakra-ui/react'
import { INFO_PANEL_PADDING } from '@editor/config/styles'
import { IntervalsAxisConfig } from '@editor/interfaces/inspectionItemGrid'
import { clamp } from 'lodash'
import { useDebouncedCallback } from 'use-debounce'

import { calculateInterval, calculateMaxPointCount, validateAxisConfigValue } from '../../utils'

let beforeChange: IntervalsAxisConfig | null = null

const EdgeBasedAxisConfig = ({
  side,
  axisConfig,
  selectedVolumeId,
  patchAxisConfig,
  onMouseIn,
  onMouseOut,
}: {
  side: string
  axisConfig: IntervalsAxisConfig
  selectedVolumeId: string
  patchAxisConfig: (axisConfig: Partial<IntervalsAxisConfig>) => void
  onMouseIn?: () => void
  onMouseOut?: () => void
}) => {
  // Refs
  const edgeDistanceInputRef = useRef<HTMLInputElement>(null)

  const pointCountInputRef = useRef<HTMLInputElement>(null)

  // States
  // max point count is managed separately as well due to side-effect of debounced patchAxisConfig.
  const [pointCountMax, setPointCountMax] = useState(axisConfig.pointCount.max)

  /**
   * Manually update point count input value.
   * @param value - New value.
   */
  const updatePointCountInput = (value: number) => {
    if (pointCountInputRef.current) {
      pointCountInputRef.current.value = value.toString()
    }
  }

  /**
   * Update point count input fields.
   *
   * @param value - New value.
   */
  const updatePointCount = useDebouncedCallback((value: number, max?: number) => {
    updatePointCountInput(value)
    if (max !== undefined) {
      setPointCountMax(max)
    }
  }, 200)

  return (
    <>
      <Flex gap={0} px={INFO_PANEL_PADDING} pt={INFO_PANEL_PADDING} onMouseEnter={onMouseIn} onMouseLeave={onMouseOut}>
        <Field.Root w="50%" invalid={!validateAxisConfigValue(axisConfig.edgeDistance)}>
          <Text>{side}の端からの距離</Text>
          <InputGroup endElement="mm">
            <Input
              ref={edgeDistanceInputRef}
              type="number"
              size="sm"
              defaultValue={axisConfig.edgeDistance.value}
              min={axisConfig.edgeDistance.min}
              max={axisConfig.edgeDistance.max}
              background="gray2.700"
              borderColor="whiteAlpha.200"
              borderRadius="5px 0 0 5px"
              disabled={!selectedVolumeId}
              onChange={(e) => {
                const value = parseInt(e.target.value, 10)
                const maxPointCount = calculateMaxPointCount(axisConfig.total, value)
                const pointCount = clamp(axisConfig.pointCount.value, axisConfig.pointCount.min, maxPointCount)
                patchAxisConfig({
                  edgeDistance: {
                    ...axisConfig.edgeDistance,
                    value,
                  },
                  pointCount: {
                    ...axisConfig.pointCount,
                    max: maxPointCount,
                    value: pointCount,
                  },
                  interval: {
                    ...axisConfig.interval,
                    value: calculateInterval(axisConfig.total, value, axisConfig.pointCount.value),
                  },
                })

                updatePointCount(pointCount, maxPointCount)
              }}
            />
          </InputGroup>
          <Box mb={2} px={2} w="full">
            <Slider.Root
              colorPalette="blue"
              defaultValue={[axisConfig.edgeDistance.value]}
              min={axisConfig.edgeDistance.min}
              max={axisConfig.edgeDistance.max}
              step={1}
              disabled={!selectedVolumeId}
              onValueChange={(e) => {
                const value = e.value[0]
                const maxPointCount = calculateMaxPointCount(axisConfig.total, value)
                const pointCount = clamp(
                  Math.max(axisConfig.pointCount.value, beforeChange?.pointCount.value || 0),
                  axisConfig.pointCount.min,
                  maxPointCount,
                )
                patchAxisConfig({
                  edgeDistance: {
                    ...axisConfig.edgeDistance,
                    value,
                  },
                  pointCount: {
                    ...axisConfig.pointCount,
                    max: maxPointCount,
                    value: pointCount,
                  },
                  interval: {
                    ...axisConfig.interval,
                    value: calculateInterval(axisConfig.total, value, axisConfig.pointCount.value),
                  },
                })

                if (edgeDistanceInputRef.current) {
                  edgeDistanceInputRef.current.value = value.toString()
                }

                updatePointCount(pointCount, maxPointCount)
              }}
              onFocusChange={(e) => {
                if (e.focusedIndex === 0) {
                  beforeChange = { ...axisConfig }
                }
              }}
            >
              <Slider.Control>
                <Slider.Track>
                  <Slider.Range />
                </Slider.Track>
                <Slider.Thumb index={0} />
              </Slider.Control>
            </Slider.Root>
          </Box>
          <Field.ErrorText fontSize="xs">
            {axisConfig.edgeDistance.min}~{axisConfig.edgeDistance.max}
            の間の値を設定してください
          </Field.ErrorText>
        </Field.Root>

        <Field.Root w="50%" invalid={!validateAxisConfigValue(axisConfig.pointCount)}>
          <Text textAlign="right">{side}の点の数</Text>
          <InputGroup endElement="点">
            <Input
              ref={pointCountInputRef}
              type="number"
              size="sm"
              defaultValue={axisConfig.pointCount.value}
              min={axisConfig.pointCount.min}
              max={pointCountMax}
              background="gray2.700"
              borderColor="whiteAlpha.200"
              borderRadius="0 5px 5px 0"
              disabled={!selectedVolumeId}
              onChange={(e) => {
                const value = parseInt(e.target.value, 10)
                patchAxisConfig({
                  pointCount: {
                    ...axisConfig.pointCount,
                    value,
                  },
                  interval: {
                    ...axisConfig.interval,
                    value: calculateInterval(axisConfig.total, axisConfig.edgeDistance.value, value),
                  },
                })

                updatePointCountInput(value)
              }}
            />
          </InputGroup>
          <Box mb={2} px={2} w="full">
            <Slider.Root
              colorPalette="blue"
              defaultValue={[axisConfig.pointCount.value]}
              min={axisConfig.pointCount.min}
              max={pointCountMax}
              step={1}
              disabled={!selectedVolumeId}
              onValueChange={(e) => {
                const value = e.value[0]
                patchAxisConfig({
                  pointCount: {
                    ...axisConfig.pointCount,
                    value,
                  },
                  interval: {
                    ...axisConfig.interval,
                    value: calculateInterval(axisConfig.total, axisConfig.edgeDistance.value, value),
                  },
                })

                updatePointCountInput(value)
              }}
            >
              <Slider.Control>
                <Slider.Track>
                  <Slider.Range />
                </Slider.Track>
                <Slider.Thumb index={0} />
              </Slider.Control>
            </Slider.Root>
          </Box>
          <Field.ErrorText fontSize="xs">
            {axisConfig.pointCount.min}~{axisConfig.pointCount.max}
            の間の値を設定してください
          </Field.ErrorText>
        </Field.Root>
      </Flex>
      <Box px={INFO_PANEL_PADDING} pb={INFO_PANEL_PADDING}>
        <Flex justifyContent="space-between">
          <Text>点の間隔</Text>
          <Text>{axisConfig.interval.value}mm</Text>
        </Flex>
      </Box>
    </>
  )
}

export default EdgeBasedAxisConfig
